import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { YEAR_GROUPS, SUBJECTS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-cream rounded-lg border-border focus-visible:ring-saffron";

export const TrialForm = () => {
    const [form, setForm] = useState({
        parent_name: "", email: "", phone: "", student_name: "",
        year_group: "", subject: "", preferred_time: "", message: "",
    });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/trial-bookings`, form);
            setDone(true);
            toast.success("Free trial requested — we will confirm your slot shortly.");
        } catch {
            toast.error("Something went wrong. Please try again or message us on WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    if (done) {
        return (
            <div className="rounded-3xl border border-border bg-sand p-10 text-center card-shadow" data-testid="trial-success-message">
                <p className="font-serif text-2xl font-semibold text-teal">Your trial is on its way!</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    We've received your request and will confirm a lesson time within 24 hours, scheduled in your time zone. Keep an eye on your inbox.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="trial-booking-form">
            <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="tr-parent">Parent's name</Label>
                    <Input id="tr-parent" data-testid="trial-parent-name-input" required value={form.parent_name} onChange={set("parent_name")} className={inputCls} placeholder="Your name" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="tr-student">Student's name</Label>
                    <Input id="tr-student" data-testid="trial-student-name-input" required value={form.student_name} onChange={set("student_name")} className={inputCls} placeholder="Child's name" />
                </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="tr-email">Email</Label>
                    <Input id="tr-email" type="email" data-testid="trial-email-input" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.co.uk" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="tr-phone">Phone / WhatsApp</Label>
                    <Input id="tr-phone" data-testid="trial-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 555 123 4567" />
                </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label>Year group</Label>
                    <Select value={form.year_group} onValueChange={set("year_group")} required>
                        <SelectTrigger className={inputCls} data-testid="trial-year-group-select">
                            <SelectValue placeholder="Select year group" />
                        </SelectTrigger>
                        <SelectContent>
                            {YEAR_GROUPS.map((y) => (
                                <SelectItem key={y} value={y} data-testid={`trial-year-option-${y.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Subject</Label>
                    <Select value={form.subject} onValueChange={set("subject")} required>
                        <SelectTrigger className={inputCls} data-testid="trial-subject-select">
                            <SelectValue placeholder="Select subject" />
                        </SelectTrigger>
                        <SelectContent>
                            {SUBJECTS.map((s) => (
                                <SelectItem key={s.id} value={s.name} data-testid={`trial-subject-option-${s.id}`}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="tr-time">Preferred day & time (your time zone)</Label>
                <Input id="tr-time" data-testid="trial-preferred-time-input" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} placeholder="e.g. Saturdays at 10am" />
            </div>
            <div className="space-y-2">
                <Label htmlFor="tr-notes">Anything we should know? (optional)</Label>
                <Textarea id="tr-notes" data-testid="trial-message-input" rows={3} value={form.message} onChange={set("message")} className={inputCls} placeholder="Current level, goals, school board…" />
            </div>
            <button
                type="submit"
                disabled={sending}
                data-testid="trial-submit-button"
                className="w-full rounded-full bg-saffron px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-saffron-dark warm-shadow disabled:opacity-60"
            >
                {sending ? "Booking…" : "Book My Free Trial Lesson"}
            </button>
            <p className="text-center text-xs text-muted-foreground">No card details needed. No obligation — just one great lesson.</p>
        </form>
    );
};
